/* Nectar Kids theme scripts. Vanilla JS, no dependencies, loaded with defer. */
(() => {
  'use strict';
  const T = window.theme || { routes: {}, strings: {} };
  const R = T.routes;
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const define = (name, cls) => { if (!customElements.get(name)) customElements.define(name, cls); };

  /* ---------- helpers ---------- */
  const formatMoney = (cents, format = T.moneyFormat || '{{amount}}') => {
    cents = Number(cents) || 0;
    const fmt = (n, d, ts = ',', ds = '.') => {
      const fixed = (n / 100).toFixed(d);
      const [i, f] = fixed.split('.');
      return i.replace(/\B(?=(\d{3})+(?!\d))/g, ts) + (f ? ds + f : '');
    };
    return format.replace(/\{\{\s*(\w+)\s*\}\}/, (_, key) => {
      switch (key) {
        case 'amount_no_decimals': return fmt(cents, 0);
        case 'amount_with_comma_separator': return fmt(cents, 2, '.', ',');
        case 'amount_no_decimals_with_comma_separator': return fmt(cents, 0, '.', ',');
        case 'amount_with_apostrophe_separator': return fmt(cents, 2, "'", '.');
        default: return fmt(cents, 2);
      }
    });
  };
  const toast = (msg) => {
    const el = document.querySelector('[data-toast]');
    if (!el) return;
    el.textContent = msg;
    el.classList.add('is-visible');
    clearTimeout(el._t);
    el._t = setTimeout(() => el.classList.remove('is-visible'), 2600);
  };
  const cartSectionIds = () => [...document.querySelectorAll('[data-cart-section]')].map((e) => e.dataset.cartSection);
  const renderSections = (sections) => {
    if (!sections) return;
    Object.entries(sections).forEach(([id, html]) => {
      if (!html) return;
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const fresh = doc.querySelector('.cart-inner');
      document.querySelectorAll(`[data-cart-section="${id}"] .cart-inner`).forEach((old) => {
        if (fresh) old.replaceWith(fresh.cloneNode(true));
      });
    });
  };
  const updateCount = (count) => {
    document.querySelectorAll('[data-cart-count]').forEach((el) => { el.textContent = count; });
  };
  const postJSON = async (url, body) => {
    const res = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body)
    });
    const data = await res.json();
    if (!res.ok || data.status) throw new Error(data.description || data.message || T.strings.error);
    return data;
  };
  const refreshCart = async () => {
    const res = await fetch(`${R.cart}.js`);
    const cart = await res.json();
    updateCount(cart.item_count);
    return cart;
  };
  const afterAdd = async (data) => {
    renderSections(data.sections);
    await refreshCart();
    document.dispatchEvent(new CustomEvent('cart:updated'));
    const drawer = document.querySelector('cart-drawer');
    if (T.cartType === 'drawer' && drawer) drawer.open();
    else toast(T.strings.added);
  };
  window.themeCart = { add: async (items) => afterAdd(await postJSON(`${R.cartAdd}.js`, { items, sections: cartSectionIds() })) };

  /* ---------- trap focus ---------- */
  const focusables = (root) => [...root.querySelectorAll('a[href],button:not([disabled]),input:not([disabled]):not([type=hidden]),select,textarea,[tabindex]:not([tabindex="-1"])')].filter((e) => e.offsetParent !== null);

  /* ---------- announcement rotator ---------- */
  define('cn-rotator', class extends HTMLElement {
    connectedCallback() {
      this.items = [...this.querySelectorAll('.announce__msg')];
      if (this.items.length < 2) return;
      this.i = 0;
      this.querySelector('[data-prev]')?.addEventListener('click', () => this.go(this.i - 1, true));
      this.querySelector('[data-next]')?.addEventListener('click', () => this.go(this.i + 1, true));
      this.addEventListener('mouseenter', () => this.stop());
      this.addEventListener('mouseleave', () => this.play());
      this.addEventListener('focusin', () => this.stop());
      this.play();
    }
    go(n) {
      this.items[this.i].classList.remove('is-active');
      this.i = (n + this.items.length) % this.items.length;
      this.items[this.i].classList.add('is-active');
    }
    play() { if (!reduceMotion) { this.stop(); this.timer = setInterval(() => this.go(this.i + 1), Number(this.dataset.interval) || 5000); } }
    stop() { clearInterval(this.timer); }
    disconnectedCallback() { this.stop(); }
  });

  /* ---------- sticky header (shadow + drawer handling) ---------- */
  define('sticky-header', class extends HTMLElement {
    connectedCallback() {
      const drawer = this.querySelector('[data-mdrawer]');
      if (drawer) {
        this.querySelector('[data-mdrawer-close]')?.addEventListener('click', () => { drawer.open = false; drawer.querySelector('summary').focus(); });
        drawer.addEventListener('toggle', () => document.body.classList.toggle('no-scroll', drawer.open));
        drawer.addEventListener('keydown', (e) => { if (e.key === 'Escape') { drawer.open = false; drawer.querySelector('summary').focus(); } });
      }
      // close other dropdowns when one opens, and on outside click / escape
      const dds = [...this.querySelectorAll('.nav__dd, .hsearch')];
      dds.forEach((d) => d.addEventListener('toggle', () => {
        if (d.open) {
          dds.forEach((o) => { if (o !== d) o.open = false; });
          d.querySelector('input[type=search]')?.focus();
        }
      }));
      document.addEventListener('click', (e) => { dds.forEach((d) => { if (d.open && !d.contains(e.target)) d.open = false; }); });
      this.addEventListener('keydown', (e) => {
        if (e.key !== 'Escape') return;
        dds.forEach((d) => { if (d.open) { d.open = false; d.querySelector('summary').focus(); } });
      });
    }
  });

  /* ---------- hero slider ---------- */
  define('cn-slider', class extends HTMLElement {
    connectedCallback() {
      this.slides = [...this.querySelectorAll('[data-slide]')];
      if (this.slides.length < 2) return;
      this.i = Math.max(0, this.slides.findIndex((s) => s.classList.contains('is-active')));
      const dots = this.querySelector('[data-dots]');
      if (dots) {
        this.dots = this.slides.map((_, n) => {
          const b = document.createElement('button');
          b.type = 'button';
          b.setAttribute('aria-label', `${n + 1} / ${this.slides.length}`);
          b.addEventListener('click', () => this.go(n));
          dots.appendChild(b);
          return b;
        });
      }
      this.querySelector('[data-prev]')?.addEventListener('click', () => this.go(this.i - 1));
      this.querySelector('[data-next]')?.addEventListener('click', () => this.go(this.i + 1));
      this.addEventListener('mouseenter', () => this.stop());
      this.addEventListener('mouseleave', () => this.play());
      this.addEventListener('focusin', () => this.stop());
      let x0 = null;
      this.addEventListener('touchstart', (e) => { x0 = e.touches[0].clientX; }, { passive: true });
      this.addEventListener('touchend', (e) => {
        if (x0 === null) return;
        const dx = e.changedTouches[0].clientX - x0;
        if (Math.abs(dx) > 40) this.go(this.i + (dx < 0 ? 1 : -1));
        x0 = null;
      });
      this.go(this.i);
      this.play();
      // theme editor: jump to the selected slide
      document.addEventListener('shopify:block:select', (e) => {
        const n = this.slides.findIndex((s) => s.contains(e.target) || s === e.target);
        if (n > -1) { this.stop(); this.go(n); }
      });
    }
    go(n) {
      this.slides[this.i].classList.remove('is-active');
      this.slides[this.i].setAttribute('aria-hidden', 'true');
      this.i = (n + this.slides.length) % this.slides.length;
      const s = this.slides[this.i];
      s.classList.add('is-active');
      s.removeAttribute('aria-hidden');
      this.slides.forEach((sl) => sl.toggleAttribute('inert', sl !== s));
      this.dots?.forEach((d, k) => d.setAttribute('aria-current', k === this.i ? 'true' : 'false'));
    }
    play() {
      if (this.dataset.autoplay !== 'true' || reduceMotion || window.Shopify?.designMode) return;
      this.stop();
      this.timer = setInterval(() => this.go(this.i + 1), Number(this.dataset.interval) || 6000);
    }
    stop() { clearInterval(this.timer); }
    disconnectedCallback() { this.stop(); }
  });

  /* ---------- accessible tabs (spotlight, age guide) ---------- */
  define('cn-tabs', class extends HTMLElement {
    connectedCallback() {
      this.tabs = [...this.querySelectorAll('[role="tab"]')];
      this.tabs.forEach((t, n) => {
        t.addEventListener('click', () => this.select(n));
        t.addEventListener('keydown', (e) => {
          const map = { ArrowRight: 1, ArrowDown: 1, ArrowLeft: -1, ArrowUp: -1 };
          if (map[e.key]) { e.preventDefault(); this.select((n + map[e.key] + this.tabs.length) % this.tabs.length, true); }
          if (e.key === 'Home') { e.preventDefault(); this.select(0, true); }
          if (e.key === 'End') { e.preventDefault(); this.select(this.tabs.length - 1, true); }
        });
      });
      document.addEventListener('shopify:block:select', (e) => {
        const n = this.tabs.findIndex((t) => t.getAttribute('aria-controls') === e.target.id);
        if (n > -1) this.select(n);
      });
    }
    select(n, focus) {
      this.tabs.forEach((t, k) => {
        const on = k === n;
        t.setAttribute('aria-selected', on);
        t.tabIndex = on ? 0 : -1;
        const panel = document.getElementById(t.getAttribute('aria-controls'));
        if (panel) panel.hidden = !on;
      });
      if (focus) this.tabs[n].focus();
    }
  });

  /* ---------- horizontal scroller ---------- */
  define('cn-scroller', class extends HTMLElement {
    connectedCallback() {
      const track = this.querySelector('[data-track]');
      const step = () => (track.firstElementChild?.getBoundingClientRect().width || 220) + 18;
      this.querySelector('[data-prev]')?.addEventListener('click', () => track.scrollBy({ left: -step(), behavior: 'smooth' }));
      this.querySelector('[data-next]')?.addEventListener('click', () => track.scrollBy({ left: step(), behavior: 'smooth' }));
    }
  });

  /* ---------- video reels (load video only on click) ---------- */
  define('video-reel', class extends HTMLElement {
    connectedCallback() {
      const btn = this.querySelector('[data-play]');
      const tpl = this.querySelector('template');
      if (!btn || !tpl) return;
      btn.addEventListener('click', () => {
        document.querySelectorAll('video-reel.is-active').forEach((r) => r !== this && r.reset());
        this.poster = btn;
        btn.replaceWith(tpl.content.cloneNode(true));
        this.classList.add('is-active');
        this.closest('.reel')?.classList.add('is-playing');
        this.querySelector('video')?.play?.();
      });
    }
    reset() {
      if (!this.poster) return;
      this.querySelectorAll('video,iframe').forEach((m) => m.remove());
      this.appendChild(this.poster);
      this.classList.remove('is-active');
      this.closest('.reel')?.classList.remove('is-playing');
    }
  });

  /* ---------- quantity ---------- */
  define('quantity-input', class extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('input');
      this.querySelectorAll('button').forEach((b) => b.addEventListener('click', () => {
        const min = Number(this.input.min) || 0;
        const max = this.input.max ? Number(this.input.max) : Infinity;
        const v = Number(this.input.value) || 0;
        const next = b.name === 'plus' ? Math.min(v + 1, max) : Math.max(v - 1, min);
        if (next === v) return;
        this.input.value = next;
        this.input.dispatchEvent(new Event('change', { bubbles: true }));
      }));
      if (this.input.dataset.line) {
        let t;
        this.input.addEventListener('change', () => {
          clearTimeout(t);
          t = setTimeout(() => changeLine(this.input.dataset.line, Number(this.input.value)), 350);
        });
      }
    }
  });

  const changeLine = async (line, quantity) => {
    document.querySelectorAll('.cart-inner').forEach((c) => c.classList.add('is-loading'));
    try {
      const data = await postJSON(`${R.cartChange}.js`, { line: Number(line), quantity, sections: cartSectionIds() });
      renderSections(data.sections);
      updateCount(data.item_count);
      document.dispatchEvent(new CustomEvent('cart:updated'));
    } catch (e) {
      toast(e.message || T.strings.error);
    } finally {
      document.querySelectorAll('.cart-inner').forEach((c) => c.classList.remove('is-loading'));
    }
  };

  define('cart-remove', class extends HTMLElement {
    connectedCallback() {
      this.querySelector('button')?.addEventListener('click', (e) => changeLine(e.currentTarget.dataset.line, 0));
    }
  });

  /* ---------- product form (AJAX add to cart, falls back to normal submit) ---------- */
  define('product-form', class extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('form');
      if (!this.form) return;
      this.form.addEventListener('submit', (e) => this.onSubmit(e));
    }
    async onSubmit(e) {
      if (e.submitter && e.submitter.name !== 'add' && e.submitter.type === 'submit' && e.submitter.name) return;
      e.preventDefault();
      const btn = this.form.querySelector('[name="add"]');
      btn?.classList.add('is-loading');
      btn?.setAttribute('aria-busy', 'true');
      try {
        const fd = new FormData(this.form);
        // include inputs that live outside the form but point at it (quantity block)
        if (this.form.id) {
          document.querySelectorAll(`[form="${this.form.id}"]`).forEach((el) => { if (el.name && !el.disabled) fd.set(el.name, el.value); });
        }
        const sections = cartSectionIds();
        if (sections.length) fd.append('sections', sections.join(','));
        const res = await fetch(`${R.cartAdd}.js`, { method: 'POST', headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' }, body: fd });
        const data = await res.json();
        if (!res.ok || data.status) throw new Error(data.description || data.message);
        await afterAdd(data);
      } catch (err) {
        toast(err.message || T.strings.error);
      } finally {
        btn?.classList.remove('is-loading');
        btn?.removeAttribute('aria-busy');
      }
    }
  });

  /* ---------- selling plan picker ---------- */
  const syncPlan = (root, form) => {
    const hidden = form?.querySelector('[data-selling-plan]');
    if (!hidden) return;
    const checked = root.querySelector('[data-plan-picker] input:checked');
    const val = checked ? checked.value : '';
    hidden.value = val;
    hidden.disabled = !val;
  };
  define('plan-picker', class extends HTMLElement {
    connectedCallback() {
      const form = document.getElementById(this.dataset.form);
      this.addEventListener('change', () => syncPlan(this, form));
      syncPlan(this, form);
    }
  });
  // inline plan pickers (spotlight) live inside the form
  document.addEventListener('change', (e) => {
    const picker = e.target.closest('[data-plan-picker]');
    if (!picker || picker.closest('plan-picker')) return;
    syncPlan(picker.parentElement, picker.closest('form'));
  });
  document.querySelectorAll('[data-plan-picker]').forEach((p) => { if (!p.closest('plan-picker')) syncPlan(p.parentElement, p.closest('form')); });

  /* ---------- variant picker ---------- */
  define('variant-picker', class extends HTMLElement {
    connectedCallback() {
      this.info = this.closest('product-info');
      this.variants = JSON.parse(this.info?.querySelector('[data-variants]')?.textContent || '[]');
      this.form = document.getElementById(this.dataset.form);
      this.addEventListener('change', () => this.update());
      this.markAvailability();
    }
    selected() {
      const opts = [];
      this.querySelectorAll('select[data-option-index]').forEach((s) => { opts[Number(s.dataset.optionIndex)] = s.value; });
      this.querySelectorAll('input[data-option-index]:checked').forEach((i) => { opts[Number(i.dataset.optionIndex)] = i.value; });
      return opts;
    }
    markAvailability() {
      const sel = this.selected();
      this.querySelectorAll('input[data-option-index]').forEach((input) => {
        const idx = Number(input.dataset.optionIndex);
        const test = [...sel];
        test[idx] = input.value;
        const match = this.variants.find((v) => v.options.every((o, k) => k === idx || o === test[k]) && v.options[idx] === input.value);
        input.classList.toggle('is-unavailable', !match || !match.available);
      });
    }
    update() {
      const opts = this.selected();
      const v = this.variants.find((x) => x.options.every((o, k) => o === opts[k]));
      this.markAvailability();
      const btn = this.form?.querySelector('[data-add-button]');
      if (!v) {
        if (btn) { btn.disabled = true; btn.textContent = T.strings.unavailable; }
        return;
      }
      const idInput = this.form?.querySelector('[data-variant-id]');
      if (idInput) idInput.value = v.id;
      if (btn) { btn.disabled = !v.available; btn.textContent = v.available ? T.strings.addToCart : T.strings.soldOut; }
      const priceWrap = this.info.querySelector('[data-price-block] [data-price-wrap]');
      if (priceWrap) {
        const onSale = v.compare_at_price && v.compare_at_price > v.price;
        priceWrap.classList.toggle('price--sale', !!onSale);
        priceWrap.querySelector('[data-price]').textContent = formatMoney(v.price);
        const cmp = priceWrap.querySelector('[data-compare]');
        if (cmp) { cmp.hidden = !onSale; cmp.textContent = onSale ? formatMoney(v.compare_at_price) : ''; }
        const off = priceWrap.querySelector('[data-discount]');
        if (off) { off.hidden = !onSale; off.textContent = onSale ? `${Math.round(((v.compare_at_price - v.price) * 100) / v.compare_at_price)}% OFF` : ''; }
      }
      if (v.featured_media) this.info.querySelector('media-gallery')?.show(v.featured_media.id);
      const url = new URL(window.location.href);
      url.searchParams.set('variant', v.id);
      window.history.replaceState({}, '', url);
      // refresh subscription prices for this variant
      const sectionId = this.dataset.section;
      if (this.info.querySelector('plan-picker') && sectionId) {
        fetch(`${this.info.dataset.url}?variant=${v.id}&section_id=${sectionId}`).then((r) => r.text()).then((html) => {
          const doc = new DOMParser().parseFromString(html, 'text/html');
          const fresh = doc.querySelector('plan-picker');
          const current = this.info.querySelector('plan-picker');
          if (fresh && current) {
            const chosen = current.querySelector('input:checked')?.value;
            current.innerHTML = fresh.innerHTML;
            if (chosen !== undefined) { const i = current.querySelector(`input[value="${chosen}"]`); if (i) i.checked = true; }
            syncPlan(current, this.form);
          }
        }).catch(() => {});
      }
    }
  });

  /* ---------- media gallery ---------- */
  define('media-gallery', class extends HTMLElement {
    connectedCallback() {
      this.querySelectorAll('[data-thumb]').forEach((b) => b.addEventListener('click', () => this.show(b.dataset.thumb)));
    }
    show(id) {
      const slide = this.querySelector(`[data-media-id="${id}"]`);
      if (!slide) return;
      this.querySelectorAll('.gallery__slide').forEach((s) => {
        const on = s === slide;
        s.classList.toggle('is-active', on);
        if (!on) s.querySelectorAll('video').forEach((v) => v.pause());
      });
      this.querySelectorAll('[data-thumb]').forEach((b) => b.setAttribute('aria-current', b.dataset.thumb === String(id)));
    }
  });

  /* ---------- product info: track recently viewed ---------- */
  define('product-info', class extends HTMLElement {
    connectedCallback() {
      const h = this.dataset.handle;
      if (!h) return;
      try {
        const key = 'theme:recently-viewed';
        const list = JSON.parse(localStorage.getItem(key) || '[]').filter((x) => x !== h);
        list.unshift(h);
        localStorage.setItem(key, JSON.stringify(list.slice(0, 12)));
      } catch (e) { /* storage unavailable */ }
    }
  });

  define('recently-viewed', class extends HTMLElement {
    async connectedCallback() {
      let list = [];
      try { list = JSON.parse(localStorage.getItem('theme:recently-viewed') || '[]'); } catch (e) { return; }
      list = list.filter((h) => h !== this.dataset.current).slice(0, Number(this.dataset.limit) || 4);
      if (!list.length) return;
      const grid = this.querySelector('[data-grid]');
      const cards = await Promise.all(list.map((h) => fetch(`${R.root.replace(/\/$/, '')}/products/${encodeURIComponent(h)}?view=card`).then((r) => (r.ok ? r.text() : '')).catch(() => '')));
      const html = cards.filter((c) => c.includes('pcard')).join('');
      if (!html) return;
      grid.innerHTML = html;
      this.hidden = false;
    }
  });

  /* ---------- recommendations ---------- */
  define('product-recommendations', class extends HTMLElement {
    connectedCallback() {
      if (this.children.length || !this.dataset.url) return;
      const io = new IntersectionObserver((entries) => {
        if (!entries[0].isIntersecting) return;
        io.disconnect();
        fetch(this.dataset.url).then((r) => r.text()).then((html) => {
          const doc = new DOMParser().parseFromString(html, 'text/html');
          const fresh = doc.querySelector('product-recommendations');
          if (fresh && fresh.innerHTML.trim()) this.innerHTML = fresh.innerHTML;
          // Empty result: a block inside the product section (e.g. "Pairs well with") only removes itself.
          // Only a standalone recommendations section hides its whole section.
          else if (this.closest('product-info')) this.remove();
          else this.closest('.cn-section')?.setAttribute('hidden', '');
        }).catch(() => {});
      }, { rootMargin: '0px 0px 400px 0px' });
      io.observe(this);
    }
  });

  /* ---------- cart drawer ---------- */
  define('cart-drawer', class extends HTMLElement {
    connectedCallback() {
      document.querySelectorAll('[data-cart-toggle]').forEach((a) => a.addEventListener('click', (e) => {
        if (document.body.classList.contains('template-cart')) return;
        e.preventDefault();
        this.opener = a;
        this.open();
      }));
      this.addEventListener('click', (e) => { if (e.target.closest('[data-drawer-close]')) this.close(); });
      document.addEventListener('keydown', (e) => { if (e.key === 'Escape' && this.classList.contains('is-open')) this.close(); });
      this.addEventListener('keydown', (e) => {
        if (e.key !== 'Tab') return;
        const f = focusables(this.querySelector('.drawer__panel'));
        if (!f.length) return;
        if (e.shiftKey && document.activeElement === f[0]) { e.preventDefault(); f[f.length - 1].focus(); }
        else if (!e.shiftKey && document.activeElement === f[f.length - 1]) { e.preventDefault(); f[0].focus(); }
      });
      document.addEventListener('shopify:section:select', (e) => { if (e.target.contains(this)) this.open(); });
      document.addEventListener('shopify:section:deselect', (e) => { if (e.target.contains(this)) this.close(); });
    }
    open() {
      this.opener = this.opener || document.activeElement;
      this.classList.add('is-open');
      this.setAttribute('aria-hidden', 'false');
      document.body.classList.add('no-scroll');
      setTimeout(() => this.querySelector('.drawer__panel')?.focus(), 50);
    }
    close() {
      this.classList.remove('is-open');
      this.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('no-scroll');
      this.opener?.focus?.();
      this.opener = null;
    }
  });

  // cart note autosave
  document.addEventListener('change', (e) => {
    if (!e.target.matches('[data-cart-note]')) return;
    postJSON(`${R.cart}/update.js`, { note: e.target.value }).catch(() => {});
  });

  /* ---------- bundle builder ---------- */
  define('bundle-builder', class extends HTMLElement {
    connectedCallback() {
      this.size = Number(this.dataset.size) || 3;
      this.box = [];
      this.slots = [...this.querySelectorAll('[data-slot]')];
      this.labels = this.slots.map((s) => s.textContent);
      this.note = this.querySelector('[data-note]');
      this.addBtn = this.querySelector('[data-add-box]');
      this.addEventListener('click', (e) => {
        const pick = e.target.closest('[data-pick]');
        if (pick && this.box.length < this.size) {
          const row = pick.closest('.pickrow');
          this.box.push({ id: pick.dataset.id, price: Number(pick.dataset.price), title: pick.dataset.title, color: pick.dataset.color, visual: row.querySelector('template[data-visual]')?.innerHTML || '' });
          this.render();
        } else if (pick) {
          toast(this.note.dataset.ready);
        }
        const x = e.target.closest('[data-remove]');
        if (x) { this.box.splice(Number(x.dataset.remove), 1); this.render(); }
      });
      this.addBtn?.addEventListener('click', () => this.addAll());
    }
    render() {
      this.slots.forEach((s, k) => {
        const item = this.box[k];
        s.classList.toggle('filled', !!item);
        s.style.setProperty('--c', item ? item.color : '');
        s.innerHTML = item
          ? `<span class="slot__v">${item.visual}</span><span class="vh">${item.title}</span><button type="button" class="slot__x" data-remove="${k}" aria-label="Remove ${item.title}">&times;</button>`
          : this.labels[k];
      });
      const left = this.size - this.box.length;
      this.note.textContent = left > 0 ? this.note.dataset.remaining.replace('[count]', left) : this.note.dataset.ready;
      this.querySelector('[data-bar]').style.width = `${(this.box.length / this.size) * 100}%`;
      this.querySelector('[data-total]').textContent = formatMoney(this.box.reduce((a, b) => a + b.price, 0));
      this.querySelectorAll('[data-pick]').forEach((b) => { b.toggleAttribute('aria-disabled', left === 0); });
      if (this.addBtn) this.addBtn.disabled = left !== 0;
    }
    async addAll() {
      const counts = {};
      this.box.forEach((i) => { counts[i.id] = (counts[i.id] || 0) + 1; });
      const items = Object.entries(counts).map(([id, quantity]) => ({ id: Number(id), quantity, properties: { _bundle: 'Build your own box' } }));
      this.addBtn.classList.add('is-loading');
      try {
        await window.themeCart.add(items);
        this.box = [];
        this.render();
      } catch (e) {
        toast(e.message || T.strings.error);
      } finally {
        this.addBtn.classList.remove('is-loading');
      }
    }
  });

  /* ---------- quiz ---------- */
  define('product-quiz', class extends HTMLElement {
    connectedCallback() {
      this.panel = this.querySelector('.quiz');
      this.openBtn = this.querySelector('[data-quiz-open]');
      this.steps = [...this.querySelectorAll('[data-step]')];
      this.results = [...this.querySelectorAll('[data-result]')];
      this.openBtn?.addEventListener('click', () => {
        const willOpen = this.panel.hidden;
        this.panel.hidden = !willOpen;
        this.openBtn.setAttribute('aria-expanded', willOpen);
        if (willOpen) { this.reset(); this.panel.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'nearest' }); }
      });
      this.addEventListener('click', (e) => {
        const a = e.target.closest('[data-answer]');
        if (a) this.answer(a);
        if (e.target.closest('[data-quiz-restart]')) this.reset();
      });
      document.addEventListener('shopify:section:select', (e) => { if (e.target.contains(this)) { this.panel.hidden = false; this.reset(); } });
    }
    reset() {
      this.votes = {};
      this.last = '';
      this.results.forEach((r) => { r.hidden = true; });
      this.steps.forEach((s, k) => { s.hidden = k !== 0; });
      this.steps[0]?.querySelector('button')?.focus();
    }
    answer(btn) {
      const key = btn.dataset.answer;
      if (key) { this.votes[key] = (this.votes[key] || 0) + 1; this.last = key; }
      const step = btn.closest('[data-step]');
      const n = this.steps.indexOf(step);
      step.hidden = true;
      if (n < this.steps.length - 1) {
        this.steps[n + 1].hidden = false;
        this.steps[n + 1].querySelector('button')?.focus();
        return;
      }
      let best = this.last;
      let max = 0;
      Object.entries(this.votes).forEach(([k, v]) => { if (v > max || (v === max && k === this.last)) { max = v; best = k; } });
      const res = this.results.find((r) => r.dataset.result === best) || this.results[0];
      if (res) { res.hidden = false; res.querySelector('a,button')?.focus(); }
    }
  });

  /* ---------- rewards calculator ---------- */
  define('rewards-calc', class extends HTMLElement {
    connectedCallback() {
      this.range = this.querySelector('[data-range]');
      this.range?.addEventListener('input', () => this.update());
      this.update();
    }
    update() {
      const d = this.dataset;
      const orders = Number(this.range.value);
      const months = Number(d.months) || 12;
      const points = Math.round(orders * months * (Number(d.order) || 0) / 100 * (Number(d.rate) || 0));
      const value = Math.round(points * (Number(d.value) || 0));
      this.querySelector('[data-out]').textContent = points.toLocaleString();
      this.querySelector('[data-orders]').textContent = orders;
      const note = this.querySelector('[data-note]');
      note.textContent = (note.dataset.template || '').replace('[points]', points.toLocaleString()).replace('[months]', months).replace('[value]', `${d.currency || ''}${value.toLocaleString()}`);
    }
  });

  /* ---------- share ---------- */
  define('share-button', class extends HTMLElement {
    connectedCallback() {
      this.querySelector('button')?.addEventListener('click', async () => {
        const data = { title: this.dataset.title, url: this.dataset.url };
        try {
          if (navigator.share) await navigator.share(data);
          else { await navigator.clipboard.writeText(data.url); toast('Link copied'); }
        } catch (e) { /* cancelled */ }
      });
    }
  });

  /* ---------- filters: auto submit ---------- */
  define('facet-form', class extends HTMLElement {
    connectedCallback() {
      const form = this.querySelector('form');
      let t;
      form?.addEventListener('change', () => {
        clearTimeout(t);
        t = setTimeout(() => {
          const params = new URLSearchParams(new FormData(form));
          [...params.keys()].forEach((k) => { if (params.get(k) === '') params.delete(k); });
          window.location.search = params.toString();
        }, 300);
      });
    }
  });
})();
